import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, Trash, Upload } from 'lucide-react';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx';
const MAX_BYTES = 25 * 1024 * 1024;

async function apiVc(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

export function buildFilesScopeFromRow(row) {
  if (!row?.primeiro) return null;
  const isGroup = row.hasGrupoVisual;
  const headerTitle = isGroup ? row.grupoNome : row.primeiro.razaoSocial;
  const sublabel = isGroup
    ? `${row.primeiro.razaoSocial} · ${row.primeiro.codigo} · ${row.primeiro.cnpj}`
    : `${row.primeiro.codigo} · ${row.primeiro.cnpj}`;
  const codigosCliente = [row.primeiro?.codigo, ...(row.outros || []).map((o) => o.codigo)].filter(
    Boolean
  );
  return {
    headerTitle,
    isGroup,
    sublabel,
    idGrupoRef: row.idGrupoRef,
    codigosCliente,
  };
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function formatFileDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

function extLabel(ext, fileName) {
  const fromExt = String(ext || '').trim().toLowerCase();
  if (fromExt) return fromExt.slice(0, 3).toUpperCase();
  const m = /\.([a-z0-9]+)$/i.exec(String(fileName || ''));
  return m ? m[1].slice(0, 3).toUpperCase() : '?';
}

export default function CustomerFilesPanel({ modal, onListagemRefresh, embedded = false }) {
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingContrato, setUploadingContrato] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [query, setQuery] = useState('');
  const [dragOverCodigo, setDragOverCodigo] = useState(null);
  const fileInputRef = useRef(null);
  const pendingContratoRef = useRef(null);

  const scopeQuery = useCallback(() => {
    const q = new URLSearchParams({
      idGrupoRef: modal.idGrupoRef,
      codigos: modal.codigosCliente.join(','),
      isGrupo: modal.isGroup ? '1' : '0',
    });
    return q.toString();
  }, [modal]);

  const loadContratos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const j = await apiVc(`/arquivos/contratos?${scopeQuery()}`);
      setContratos(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      setError(e.message || 'Falha ao carregar contratos');
      setContratos([]);
    } finally {
      setLoading(false);
    }
  }, [scopeQuery]);

  useEffect(() => {
    loadContratos();
  }, [loadContratos]);

  const filteredContratos = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contratos;
    return contratos.filter((c) => {
      const blob = `${c.codigo || ''} ${c.nome || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [contratos, query]);

  const uploadFile = useCallback(
    async (file, codigoContrato) => {
      if (!file || !codigoContrato) return;
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
      if (!allowed.includes(ext)) {
        window.alert('Extensão não permitida. Use pdf, doc, docx, xls ou xlsx.');
        return;
      }
      if (file.size > MAX_BYTES) {
        window.alert('Arquivo acima de 25MB.');
        return;
      }

      setUploadingContrato(codigoContrato);
      try {
        const presign = await apiVc(
          `/arquivos/contrato/${encodeURIComponent(codigoContrato)}/presign`,
          {
            method: 'POST',
            body: {
              fileName: file.name,
              mimeType: file.type || 'application/octet-stream',
              sizeBytes: file.size,
              isGrupo: modal.isGroup,
              idGrupoRef: modal.idGrupoRef,
              codigosCliente: modal.codigosCliente,
            },
          }
        );
        const { uploadUrl, key, mime } = presign.data;
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': mime || file.type },
          body: file,
        });
        if (!uploadRes.ok) throw new Error('Falha ao enviar arquivo para o armazenamento');

        const done = await apiVc(
          `/arquivos/contrato/${encodeURIComponent(codigoContrato)}/complete`,
          {
            method: 'POST',
            body: {
              s3Key: key,
              fileName: file.name,
              mimeType: file.type || mime,
              sizeBytes: file.size,
              isGrupo: modal.isGroup,
              idGrupoRef: modal.idGrupoRef,
              codigosCliente: modal.codigosCliente,
              codigoCliente: presign.data.codigoCliente,
            },
          }
        );
        if (done.listagem && onListagemRefresh) onListagemRefresh(done.listagem);
        await loadContratos();
      } catch (err) {
        window.alert(err.message || 'Erro ao enviar arquivo');
      } finally {
        setUploadingContrato(null);
        pendingContratoRef.current = null;
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [loadContratos, modal, onListagemRefresh]
  );

  const triggerUpload = (codigoContrato) => {
    pendingContratoRef.current = codigoContrato;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    uploadFile(file, pendingContratoRef.current);
  };

  const handleDrop = (e, codigoContrato) => {
    e.preventDefault();
    setDragOverCodigo(null);
    const file = e.dataTransfer?.files?.[0];
    uploadFile(file, codigoContrato);
  };

  const requestDelete = async (arquivo) => {
    if (!window.confirm(`Excluir o arquivo "${arquivo.nomeOriginal}"?`)) return;
    setDeletingId(arquivo.id);
    try {
      const j = await apiVc(`/arquivos/${arquivo.id}?${scopeQuery()}`, { method: 'DELETE' });
      if (j.listagem && onListagemRefresh) onListagemRefresh(j.listagem);
      await loadContratos();
    } catch (err) {
      window.alert(err.message || 'Erro ao excluir');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={embedded ? 'uc-files-panel-embedded' : 'uc-files-panel'}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="uc-files-input-hidden"
        onChange={handleFileChange}
        aria-hidden
        tabIndex={-1}
      />

      <div className="uc-files-v2-toolbar">
        <div className="uc-files-v2-search-wrap">
          <Search size={14} strokeWidth={2} className="uc-files-v2-search-icon" aria-hidden />
          <input
            type="search"
            className="uc-files-v2-search uc-cg-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por número, empenho ou descrição do contrato…"
            aria-label="Filtrar contratos"
            disabled={loading}
          />
        </div>
      </div>

      <div
        className={`uc-files-v2-scroll${embedded ? ' uc-files-v2-scroll--embedded' : ''}`}
        role="region"
        aria-label="Contratos"
      >
        {loading ? (
          <div className="uc-files-v2-empty">
            <Loader2 className="users-customers-modal-spinner" size={20} />
            <span>Carregando contratos…</span>
          </div>
        ) : error ? (
          <div className="uc-files-v2-empty uc-files-v2-empty--error">{error}</div>
        ) : filteredContratos.length === 0 ? (
          <div className="uc-files-v2-empty">
            {query.trim()
              ? 'Nenhum contrato encontrado para essa busca.'
              : 'Nenhum contrato ativo encontrado.'}
          </div>
        ) : (
          filteredContratos.map((ct) => {
            const busy = uploadingContrato === ct.codigo;
            const arquivos = ct.arquivos || [];
            const isDragOver = dragOverCodigo === ct.codigo;
            return (
              <div
                key={ct.codigo}
                className={[
                  'uc-files-v2-card',
                  arquivos.length > 0 ? 'uc-files-v2-card--has-files' : '',
                  isDragOver ? 'uc-files-v2-card--drag' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverCodigo(ct.codigo);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragOverCodigo((prev) => (prev === ct.codigo ? null : prev));
                }}
                onDrop={(e) => handleDrop(e, ct.codigo)}
              >
                <div className="uc-files-v2-card-head">
                  <span className="uc-files-v2-code">{ct.codigo}</span>
                  <span className="uc-files-v2-title" title={ct.nome}>
                    {ct.nome}
                  </span>
                  {arquivos.length > 0 && (
                    <span className="uc-files-v2-file-count">
                      {arquivos.length}{' '}
                      {arquivos.length === 1 ? 'arquivo' : 'arquivos'}
                    </span>
                  )}
                  <button
                    type="button"
                    className="uc-files-v2-upload-btn"
                    onClick={() => triggerUpload(ct.codigo)}
                    disabled={Boolean(uploadingContrato)}
                    title="Enviar arquivos"
                    aria-label={`Enviar arquivo no contrato ${ct.codigo}`}
                  >
                    {busy ? (
                      <Loader2 size={14} className="users-customers-modal-spinner-inline" />
                    ) : (
                      <Upload size={14} strokeWidth={2.2} />
                    )}
                  </button>
                </div>

                {arquivos.length > 0 ? (
                  <ul className="uc-files-v2-files">
                    {arquivos.map((a) => {
                      const label = extLabel(a.extensao, a.nomeOriginal);
                      const extKey = String(a.extensao || label).toLowerCase();
                      return (
                        <li key={a.id} className="uc-files-v2-file-row">
                          <span
                            className={`uc-files-v2-ext uc-files-v2-ext--${extKey}`}
                            aria-hidden
                          >
                            <span className="uc-files-v2-ext-label">{label}</span>
                          </span>
                          {a.hasFile !== false ? (
                            <button
                              type="button"
                              className="uc-files-v2-name uc-files-v2-name--link"
                              title={a.nomeOriginal}
                              onClick={() => {
                                const url = a.viewUrl;
                                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              {a.nomeOriginal}
                            </button>
                          ) : (
                            <span className="uc-files-v2-name" title={a.nomeOriginal}>
                              {a.nomeOriginal}
                            </span>
                          )}
                          <span className="uc-files-v2-meta">
                            {formatFileSize(a.tamanhoBytes)} · {formatFileDate(a.createdAt)}
                          </span>
                          <button
                            type="button"
                            className="users-customers-delete-btn uc-files-v2-delete-btn"
                            onClick={() => requestDelete(a)}
                            disabled={deletingId === a.id}
                            aria-label={`Excluir ${a.nomeOriginal}`}
                            title="Excluir arquivo"
                          >
                            {deletingId === a.id ? (
                              <Loader2 size={13} className="users-customers-modal-spinner-inline" />
                            ) : (
                              <Trash size={13} strokeWidth={2} />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="uc-files-v2-drop-hint">
                    Arraste arquivos aqui ou clique em ↑ para anexar
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export function dialogSizeClass(totalArquivos) {
  if (totalArquivos >= 8) return 'uc-files-dialog--lg';
  if (totalArquivos >= 3) return 'uc-files-dialog--md';
  return 'uc-files-dialog--sm';
}
